/**
 * useFABPreferences — TanStack Query hooks for FAB (Floating Action Button) preferences.
 *
 * Wraps `fabPreferencesApi` from `web/src/services/api/fabPreferences.ts`.
 * staleTime: 60_000 (60 seconds) — preferences rarely change.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fabPreferencesApi } from '../../services/api/fabPreferences';
import type { FABPreferencesPatch } from '../../services/api/fabPreferences';
import { unwrapResponse } from '../../utils/unwrapResponse';
import { useOrganization } from '../../contexts/OrganizationContext';

export interface FABPreferencesData {
  actionKeys: string[] | null;
  layout: 'vertical' | 'narrow';
  onboarded: boolean;
}

export function useFABPreferences() {
  const queryClient = useQueryClient();
  const { organization } = useOrganization();
  const orgId = organization?.id ?? '';
  const queryKey = ['fab-preferences', orgId] as const;

  const query = useQuery({
    queryKey,
    queryFn: async (): Promise<FABPreferencesData> => {
      const response = await fabPreferencesApi.get();
      return unwrapResponse<FABPreferencesData>(response.data);
    },
    staleTime: 60_000,
    enabled: !!orgId,
    // Graceful fallback: if the backend endpoint isn't deployed yet, return defaults
    retry: (failureCount, error) => {
      const axiosError = error as { response?: { status?: number } };
      if (axiosError?.response?.status === 404) return false;
      return failureCount < 2;
    },
  });

  const mutation = useMutation({
    mutationFn: (patch: FABPreferencesPatch) => fabPreferencesApi.update(patch),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  return {
    preferences: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error,
    saveFABPreferences: mutation.mutate,
    isSaving: mutation.isPending,
  };
}
